/*
  Velune · página de produto do layout novo ("mm").
  Carregado por sections/product-information.liquid.

  O que este arquivo faz (o resto é do Horizon: troca de variante, galeria, zoom, formulário de compra):
  1. Opções: o nome do valor escolhido ("Color: Pink") muda na hora, sem esperar o servidor.
  2. Barra de compra presa (abaixo de 1024 px): só aparece quando o botão "Add to cart" da caixa de compra
     sai da tela; os botões de quantidade dela comandam o seletor do formulário da página.
  3. Depois de trocar de tom: mostra a foto do tom e acerta a quantidade da barra.
  4. Estrelas perto do nome: viram link para as avaliações só quando a seção de avaliações existe.
  5. Relacionados: busca as recomendações da Shopify e põe os cartões no lugar.
  6. Barra de benefícios no celular: anda sozinha a cada 10 s.
  7. Botão de comprar: estados "carregando" e "adicionado" (data-mm-state), na caixa, na barra presa e na
     janela de compra rápida.
  8. Galeria: só a primeira foto vem com a página (as outras têm loading="lazy"). Quando a cliente mexe na
     galeria, as fotos vizinhas da que está à mostra começam a baixar, para o arrasto não mostrar quadro vazio.
*/

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ */
/* 1. Opções: nome do valor escolhido                                 */
/* ------------------------------------------------------------------ */

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

  const label = input.closest('[data-mm-opt]')?.querySelector('[data-mm-opt-value]');
  if (label && label.textContent !== input.value) label.textContent = input.value;
});

/* ------------------------------------------------------------------ */
/* 2. Barra de compra presa: quando aparece e quantidade              */
/* ------------------------------------------------------------------ */

/** @type {IntersectionObserver | null} */
let buyBarObserver = null;

/**
 * A barra presa só fica à mostra enquanto o botão "Add to cart" da caixa de compra está fora da tela
 * (acima ou abaixo dela, ou escondido atrás do topo preso). Os dois nunca aparecem juntos.
 */
function initBuyBarVisibility() {
  const bar = document.querySelector('[data-mm-buybar]');
  const button = document.querySelector('.mm-pdp .mm-buy .add-to-cart-button');
  if (!(bar instanceof HTMLElement) || !(button instanceof HTMLElement) || !('IntersectionObserver' in window)) return;

  buyBarObserver?.disconnect();

  // O topo fica preso e cobre o alto da tela: essa faixa não conta como "à vista"
  const header = document.querySelector('#header-group, .header-section, header');
  const headerHeight = header instanceof HTMLElement ? Math.round(header.getBoundingClientRect().height) : 0;
  const top = Math.min(Math.max(headerHeight, 0), Math.round(window.innerHeight / 2));

  buyBarObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      bar.toggleAttribute('data-mm-show', !entry.isIntersecting);
    },
    { rootMargin: `-${top}px 0px 0px 0px`, threshold: 0 }
  );
  buyBarObserver.observe(button);
}

let buyBarResizeTimer = 0;
window.addEventListener('resize', () => {
  window.clearTimeout(buyBarResizeTimer);
  buyBarResizeTimer = window.setTimeout(initBuyBarVisibility, 200);
});

/** Seletor de quantidade do formulário de compra da página (o da coluna, que a barra comanda). */
function mainQuantitySelector() {
  return document.querySelector('.mm-pdp product-form-component quantity-selector-component');
}

function syncBuyBar() {
  const bar = document.querySelector('[data-mm-buybar]');
  if (!bar) return;

  const selector = mainQuantitySelector();
  const input = selector?.querySelector('input[name="quantity"]');
  const number = bar.querySelector('[data-mm-buybar-number]');
  const quantityGroup = bar.querySelector('.mm-buybar__qty');

  // Sem seletor na página (bloco de quantidade desligado), a barra fica só com o botão
  if (quantityGroup instanceof HTMLElement) quantityGroup.hidden = !selector;
  if (!(input instanceof HTMLInputElement) || !number) return;

  const value = String(Number.parseInt(input.value, 10) || Number.parseInt(input.min, 10) || 1);
  if (number.textContent !== value) number.textContent = value;

  for (const step of ['minus', 'plus']) {
    const barButton = bar.querySelector(`[data-mm-buybar-step="${step}"]`);
    const mainButton = selector?.querySelector(`button[name="${step}"]`);
    if (barButton instanceof HTMLButtonElement && mainButton instanceof HTMLButtonElement) {
      barButton.disabled = mainButton.disabled;
    }
  }
}

let buyBarSyncQueued = false;

function queueBuyBarSync() {
  if (buyBarSyncQueued) return;
  buyBarSyncQueued = true;
  requestAnimationFrame(() => {
    buyBarSyncQueued = false;
    syncBuyBar();
  });
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const stepButton = target?.closest('[data-mm-buybar-step]');
  if (!(stepButton instanceof HTMLElement)) return;

  event.preventDefault();
  const mainButton = mainQuantitySelector()?.querySelector(`button[name="${stepButton.dataset.mmBuybarStep}"]`);
  if (mainButton instanceof HTMLButtonElement && !mainButton.disabled) mainButton.click();
  queueBuyBarSync();
});

// O seletor do Horizon avisa quando a quantidade muda (os avisos do carrinho trazem cartLine e não interessam aqui)
document.addEventListener('quantity-selector:update', (event) => {
  if (event instanceof CustomEvent && event.detail?.cartLine) return;
  queueBuyBarSync();
});

/* ------------------------------------------------------------------ */
/* 3. Depois de trocar de tom                                         */
/* ------------------------------------------------------------------ */

/**
 * Mostra a foto do tom escolhido quando ela não é a primeira da galeria
 * (caso de quem volta para o primeiro tom: o servidor devolve a galeria começando pelas fotos gerais).
 * @param {{ featured_media?: { id?: number | string } } | null | undefined} variant
 */
function showVariantMedia(variant) {
  const mediaId = variant?.featured_media?.id;
  if (!mediaId) return;

  const gallery = document.querySelector('.mm-pdp media-gallery[data-mm-gallery]');
  if (!gallery) return;

  const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
  const index = slides.findIndex((slide) => slide.querySelector(`.product-media[data-media-id="${mediaId}"]`));
  if (index <= 0) return;

  // A foto do tom pode ainda não ter sido baixada (só a primeira vem com a página)
  primeGallerySlides(gallery, index);

  try {
    // @ts-ignore -- media-gallery do Horizon: o carrossel fica em .slideshow
    const slideshow = gallery.slideshow;
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.select(index, undefined, { animate: false });
      return;
    }
  } catch {
    /* cai na rolagem direta, abaixo */
  }

  const slide = slides[index];
  const scroller = gallery.querySelector('slideshow-slides');
  if (slide instanceof HTMLElement && scroller instanceof HTMLElement) {
    scroller.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
  }
}

/** @type {Promise<unknown> | null} A troca de variante que ainda espera a resposta da loja ("Buy now" espera por ela) */
let pendingSelect = null;

/** @param {Event} event */
function onProductSelect(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || target.closest('product-card') || !target.closest('.mm-pdp')) return;

  // @ts-ignore -- ProductSelectEvent do Horizon traz a promessa do HTML novo
  const promise = event.promise;
  if (!promise || typeof promise.then !== 'function') return;
  pendingSelect = promise;

  promise
    .then((/** @type {any} */ result) => {
      // Espera a galeria e a barra novas entrarem na página
      window.setTimeout(() => {
        syncBuyBar();
        showVariantMedia(result?.detail?.resource);
        initRatingLinks();
      }, 80);
    })
    .catch(() => {
      /* troca cancelada por outra mais nova */
    });
}

import('@shopify/events')
  .then((module) => {
    const name = module?.StandardEvents?.productSelect;
    if (typeof name === 'string') document.addEventListener(name, onProductSelect);
  })
  .catch(() => {
    /* sem o módulo de eventos, a troca de tom continua funcionando pelo Horizon */
  });

/* ------------------------------------------------------------------ */
/* 4. Estrelas: link para as avaliações                               */
/* ------------------------------------------------------------------ */

function initRatingLinks() {
  for (const link of document.querySelectorAll('[data-mm-reviews-link]')) {
    if (!(link instanceof HTMLElement)) continue;
    const selector = link.dataset.mmReviewsLink || '';
    const target = selector ? document.querySelector(selector) : null;
    if (target && !link.hasAttribute('href')) link.setAttribute('href', selector);
    else if (!target && link.hasAttribute('href')) link.removeAttribute('href');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const link = target?.closest('[data-mm-reviews-link][href]');
  if (!(link instanceof HTMLElement)) return;

  const section = document.querySelector(link.dataset.mmReviewsLink || '');
  if (!(section instanceof HTMLElement)) return;

  event.preventDefault();
  section.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
  section.focus({ preventScroll: true });
});

/* ------------------------------------------------------------------ */
/* 5. Relacionados                                                    */
/* ------------------------------------------------------------------ */

/** @param {HTMLElement} section */
async function loadRelated(section) {
  const url = section.dataset.url;
  if (!url) return;

  try {
    const response = await fetch(url);
    if (!response.ok) return;

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = html.querySelector('[data-mm-related-content]');
    const target = section.querySelector('[data-mm-related-content]');
    if (!fresh || !target) return;

    // O produto da própria página não entra na vitrine
    const productId = section.dataset.productId;
    if (productId) {
      for (const slide of fresh.querySelectorAll(`.mm-carousel__slide[data-product-id="${productId}"]`)) slide.remove();
    }
    // Os 2 produtos que a caixinha do trio (blocks/velune-trio-mini.liquid, na caixa de compra) já mostra
    // também não entram. Os ids vêm da página (data-skip-ids, a mesma regra da caixinha); sem ela, nada sai.
    const skipIds = (section.dataset.skipIds || '').split(',').filter(Boolean);
    if (skipIds.length > 0 && document.querySelector('.mm-pdp .mm-trio-mini, #complete-the-routine')) {
      for (const slide of fresh.querySelectorAll('.mm-carousel__slide[data-product-id]')) {
        if (slide instanceof HTMLElement && skipIds.includes(slide.dataset.productId || '')) slide.remove();
      }
    }
    if (!fresh.querySelector('.mm-card')) return;

    target.replaceChildren(...Array.from(fresh.childNodes).map((node) => document.importNode(node, true)));
    section.hidden = false;
  } catch {
    /* sem rede: a seção continua escondida */
  }
}

function initRelated() {
  for (const section of document.querySelectorAll('[data-mm-related][data-url]:not([data-mm-loaded])')) {
    if (!(section instanceof HTMLElement)) continue;
    section.setAttribute('data-mm-loaded', '');

    const start = () => loadRelated(section);
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2500 });
    else window.setTimeout(start, 800);
  }
}

/* ------------------------------------------------------------------ */
/* 6. Barra de benefícios: anda sozinha no celular                    */
/* ------------------------------------------------------------------ */

function initAutoScroll() {
  for (const list of document.querySelectorAll('[data-mm-autoscroll]:not([data-mm-auto])')) {
    if (!(list instanceof HTMLElement)) continue;
    list.setAttribute('data-mm-auto', '');

    const delay = Number(list.dataset.mmAutoscroll) || 10000;
    let pausedUntil = 0;
    const pause = () => {
      pausedUntil = Date.now() + 2 * delay;
    };
    list.addEventListener('pointerdown', pause, { passive: true });
    list.addEventListener('touchstart', pause, { passive: true });
    list.addEventListener('focusin', pause);

    window.setInterval(() => {
      if (!list.isConnected || document.hidden || reducedMotion.matches || Date.now() < pausedUntil) return;

      const max = list.scrollWidth - list.clientWidth;
      if (max <= 1) return; // os itens cabem (computador): nada a rolar

      const start = list.firstElementChild instanceof HTMLElement ? list.firstElementChild.offsetLeft : 0;
      const next = Array.from(list.children).find(
        (item) => item instanceof HTMLElement && item.offsetLeft - start > list.scrollLeft + 8
      );
      const left =
        list.scrollLeft >= max - 1 || !(next instanceof HTMLElement) ? 0 : Math.min(max, next.offsetLeft - start);
      list.scrollTo({ left, behavior: 'smooth' });
    }, delay);
  }
}

/* ------------------------------------------------------------------ */
/* 7. Botão de comprar: carregando e adicionado                       */
/* ------------------------------------------------------------------ */

const buyStateTimers = new WeakMap();

/**
 * @param {Element[]} buttons
 * @param {'loading' | 'added' | null} state
 */
function setBuyState(buttons, state) {
  for (const button of buttons) {
    window.clearTimeout(buyStateTimers.get(button));
    if (!state) {
      button.removeAttribute('data-mm-state');
      button.removeAttribute('aria-busy');
      continue;
    }
    button.setAttribute('data-mm-state', state);
    if (state === 'loading') button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');

    // Volta ao normal sozinho: depois de mostrar "Added", ou se a resposta nunca chegar
    buyStateTimers.set(
      button,
      window.setTimeout(() => setBuyState([button], null), state === 'added' ? 1800 : 12000)
    );
  }
}

/** @param {Event} event */
function onCartLinesUpdate(event) {
  // @ts-ignore -- CartLinesUpdateEvent do Horizon: action, context e a promessa do pedido
  const { action, promise } = event;
  const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
  if (!form || !form.closest('.mm-buy') || action !== 'add' || !promise || typeof promise.then !== 'function') return;

  const buttons = Array.from(form.querySelectorAll('.add-to-cart-button'));
  // Na página de produto, a barra presa compra pelo mesmo formulário
  if (form.closest('.mm-pdp')) buttons.push(...document.querySelectorAll('.mm-pdp .mm-buybar__add'));

  setBuyState(buttons, 'loading');
  promise
    .then((/** @type {any} */ result) => setBuyState(buttons, result?.detail?.didError ? null : 'added'))
    .catch(() => setBuyState(buttons, null));
}

/**
 * "Buy now" (blocks/buy-buttons.liquid, pedido do Eric em 05/10/2026): põe no carrinho o que está escolhido no
 * formulário de compra (variante, quantidade e o que mais o formulário levar) e abre a PÁGINA do carrinho, em
 * vez da gaveta. Se a loja recusar (estoque), a mensagem dela aparece no aviso do próprio formulário.
 * @param {Event} event
 */
async function onBuyNowClick(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-mm-buy-now]') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled || button.getAttribute('aria-busy') === 'true') return;
  const form = button.closest('form');
  if (!(form instanceof HTMLFormElement)) return;
  event.preventDefault();

  form.querySelector('[data-mm-buy-now-error]')?.remove();
  button.setAttribute('aria-busy', 'true');

  // Troca de tom ainda a caminho: espera o tema gravar a variante nova no formulário, senão iria o tom anterior
  if (pendingSelect) {
    // No máximo 4 s de espera: se a resposta não vier, segue com o que o formulário tem
    const limit = new Promise((resolve) => window.setTimeout(resolve, 4000));
    await Promise.race([Promise.resolve(pendingSelect).catch(() => {}), limit]);
    pendingSelect = null;
  }
  // Depois da troca o tom pode estar esgotado (o "Add to cart" fica desligado e o estilo esconde este botão)
  const add = button.closest('.product-form-buttons')?.querySelector('.add-to-cart-button');
  if (add instanceof HTMLButtonElement && add.disabled) {
    button.removeAttribute('aria-busy');
    return;
  }

  // @ts-ignore -- Shopify.routes vem do tema
  const root = window.Shopify?.routes?.root || '/';
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.description || data?.message || '');
    window.location.assign(`${root}cart`);
  } catch (error) {
    button.removeAttribute('aria-busy');
    const note = document.createElement('p');
    note.className = 'mm-sm mm-buy__agora-erro';
    note.setAttribute('role', 'alert');
    note.setAttribute('data-mm-buy-now-error', '');
    note.textContent = error instanceof Error && error.message ? error.message : 'Something went wrong. Please try again.';
    button.insertAdjacentElement('afterend', note);
  }
}

document.addEventListener('click', onBuyNowClick);

// Voltar do carrinho com a página devolvida como estava (comum no iPhone): o botão não pode voltar preso em "ocupado"
window.addEventListener('pageshow', () => {
  for (const button of document.querySelectorAll('[data-mm-buy-now][aria-busy]')) button.removeAttribute('aria-busy');
});

import('@shopify/events')
  .then((module) => {
    const name = module?.StandardEvents?.cartLinesUpdate;
    if (typeof name !== 'string') return;
    document.addEventListener(name, onCartLinesUpdate);
    // Avisa o estilo de que os estados são marcados daqui (snippets/add-to-cart-button-styles.liquid)
    document.documentElement.setAttribute('data-mm-buy-states', '');
  })
  .catch(() => {
    /* sem o módulo de eventos, a compra continua funcionando pelo Horizon */
  });

/* ------------------------------------------------------------------ */
/* 8. Galeria: fotos vizinhas baixam quando a cliente mexe nela       */
/* ------------------------------------------------------------------ */

/**
 * Manda baixar a foto do slide pedido e as vizinhas (uma antes, duas depois).
 * @param {Element} gallery
 * @param {number} index
 */
function primeGallerySlides(gallery, index) {
  const slides = gallery.querySelectorAll('slideshow-slide');
  for (const position of [index, index + 1, index - 1, index + 2]) {
    // Num slide de vídeo são duas imagens: a da galeria e a capa do vídeo
    for (const image of slides[position]?.querySelectorAll('img[loading="lazy"]') ?? []) {
      if (image instanceof HTMLImageElement) image.loading = 'eager';
    }
  }
}

/**
 * Posição do slide que está à mostra, pela rolagem do trilho.
 * @param {Element} gallery
 */
function currentGallerySlide(gallery) {
  const scroller = gallery.querySelector('slideshow-slides');
  const slides = scroller?.querySelectorAll('slideshow-slide');
  if (!(scroller instanceof HTMLElement) || !slides || slides.length < 2) return 0;

  const [first, second] = slides;
  if (!(first instanceof HTMLElement) || !(second instanceof HTMLElement)) return 0;

  const step = second.offsetLeft - first.offsetLeft;
  return step > 0 ? Math.round(scroller.scrollLeft / step) : 0;
}

/** @param {Event} event */
function onGalleryActivity(event) {
  const target = event.target instanceof Element ? event.target : null;
  const gallery = target?.closest('.mm-pdp media-gallery[data-mm-gallery]');
  if (gallery) primeGallerySlides(gallery, currentGallerySlide(gallery));
}

// Toque, cursor ou foco na galeria; e a rolagem do trilho (que não sobe na árvore: por isso a captura)
for (const type of ['pointerdown', 'pointerover', 'focusin']) {
  document.addEventListener(type, onGalleryActivity, { passive: true });
}
document.addEventListener('scroll', onGalleryActivity, { capture: true, passive: true });

/* ------------------------------------------------------------------ */
/* Início                                                             */
/* ------------------------------------------------------------------ */

function init() {
  syncBuyBar();
  initBuyBarVisibility();
  initRatingLinks();
  initRelated();
  initAutoScroll();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();

// As seções de baixo entram depois da principal; o editor de temas recarrega seções soltas
window.addEventListener('load', init, { once: true });
document.addEventListener('shopify:section:load', init);
